import {
  canPublishStore,
  storePublicationStatus,
} from "../utils/storePublication";
import CompanyProfileEditor from "../components/CompanyProfileEditor";
import {
  normalizeCompanyProfile,
  validateCompanyProfile,
} from "../utils/companyProfile";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MDEditor from "@uiw/react-md-editor";
import { QRCodeSVG } from "qrcode.react";
import { db, storage } from "../firebase";
import { useAuth } from "../AuthProvider";
import {
  collection,
  doc,
  runTransaction,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  getBusinessTypeLabel,
  summarizeStorePackages,
} from "../utils/storePackages";

function slugify(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeCategories(raw = "") {
  return [
    ...new Set(
      String(raw || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
}

const STORE_SETUP_STEPS = [
  {
    id: 0,
    label: "Identity",
    title: "Name your page",
    description:
      "Choose the store name, URL, bio, and categories buyers will see first.",
  },
  {
    id: 1,
    label: "Design",
    title: "Choose your storefront style",
    description:
      "Choose a storefront template, then add your cover and profile images so buyers recognise your brand immediately.",
  },
  {
    id: 2,
    label: "Payments",
    title: "Set how buyers pay you",
    description:
      "Add bank details and instructions so customers can complete checkout easily.",
  },
];

const STORE_CATEGORY_OPTIONS = [
  "Courses",
  "Products",
  "Services",
  "Digital Products",
  "Coaching",
  "Marketing",
  "Design",
  "Business",
  "Trading",
  "Technology",
  "Fashion",
  "Health",
  "Education",
];

const PREVIEW_ITEMS = [
  {
    id: "preview-course",
    title: "Pattern Making Masterclass",
    subtitle: "Course",
    price: "LKR 3,950",
  },
  {
    id: "preview-product",
    title: "Smart Bronze Pack",
    subtitle: "Product",
    price: "LKR 4,000",
  },
  {
    id: "preview-service",
    title: "Social Media Marketing Setup",
    subtitle: "Service",
    price: "LKR 2,000",
  },
];

const DEFAULT_STOREFRONT_TEMPLATES = [
  {
    id: "default-shopping-template",
    name: "Classic Shopping Front",
    templateType: "shopping",
    active: true,
    description: "A premium storefront card for shopping-complex stores.",
    imageUrl: "/img/hero-background.png",
    textColor: "#ffffff",
    titleBackground: "#2d1d16cc",
    titlePosition: "top",
  },
  {
    id: "default-university-template",
    name: "University Hall",
    templateType: "course",
    active: true,
    description:
      "A polished academic storefront for universities and course brands.",
    imageUrl: "/img/uni-background.jpeg",
    textColor: "#dff9ff",
    titleBackground: "#0d2f44dd",
    titlePosition: "top",
  },
  {
    id: "default-services-template",
    name: "Service Studio",
    templateType: "service",
    active: true,
    description:
      "A rich service-marketplace storefront for premium studios and agencies.",
    imageUrl: "/img/beauty-zone.png",
    textColor: "#ffffff",
    titleBackground: "#2f1a13dd",
    titlePosition: "top",
  },
];

function storefrontOverlayClass(position = "top") {
  if (position === "center") return "left-4 right-4 top-1/2 -translate-y-1/2";
  if (position === "bottom") return "left-4 right-4 bottom-4";
  return "left-4 right-4 top-4";
}

export function StoreSettingsPanel({
  embedded = false,
  title = "Store Settings",
  subtitle = "Set up your public store page and share it with customers.",
}) {
  const publicBaseUrl = "https://www.ceylonwebmall.com";
  const { user } = useAuth();
  const [userDoc, setUserDoc] = useState(null);
  const [storeDoc, setStoreDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyMediaUploading, setCompanyMediaUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initializingStore, setInitializingStore] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const [displayName, setDisplayName] = useState("");
  const [slug, setSlug] = useState("");
  const [bio, setBio] = useState("");
  const [storeDescription, setStoreDescription] = useState("");
  const [companyProfile, setCompanyProfile] = useState(() =>
    normalizeCompanyProfile(),
  );
  const [categories, setCategories] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [virtualStorefrontImageUrl, setVirtualStorefrontImageUrl] =
    useState("");
  const [paymentQrCodeUrl, setPaymentQrCodeUrl] = useState("");
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [virtualStorefrontImageFile, setVirtualStorefrontImageFile] =
    useState(null);
  const [paymentQrCodeFile, setPaymentQrCodeFile] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState("");
  const [coverPreviewUrl, setCoverPreviewUrl] = useState("");
  const [virtualStorefrontPreviewUrl, setVirtualStorefrontPreviewUrl] =
    useState("");
  const [paymentQrPreviewUrl, setPaymentQrPreviewUrl] = useState("");
  const [paymentBankName, setPaymentBankName] = useState("");
  const [paymentAccountHolder, setPaymentAccountHolder] = useState("");
  const [paymentAccountNumber, setPaymentAccountNumber] = useState("");
  const [paymentBranch, setPaymentBranch] = useState("");
  const [paymentPhone, setPaymentPhone] = useState("");
  const [paymentWhatsapp, setPaymentWhatsapp] = useState("");
  const [paymentInstructions, setPaymentInstructions] = useState("");
  const [paymentCashOnDelivery, setPaymentCashOnDelivery] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplateImageUrl, setSelectedTemplateImageUrl] = useState("");
  const [selectedTemplateType, setSelectedTemplateType] = useState("all");
  const [selectedTemplateTitleBackground, setSelectedTemplateTitleBackground] =
    useState("#2d1d16cc");
  const [selectedTemplateTextColor, setSelectedTemplateTextColor] =
    useState("#ffffff");
  const [selectedTemplateTitlePosition, setSelectedTemplateTitlePosition] =
    useState("top");
  const [selectedTemplateDescription, setSelectedTemplateDescription] =
    useState("");

  useEffect(() => {
    if (!user?.uid) return;

    const unsubUser = onSnapshot(doc(db, "users", user.uid), (snap) => {
      setUserDoc(snap.exists() ? snap.data() : null);
    });

    const unsubStore = onSnapshot(doc(db, "stores", user.uid), (snap) => {
      setStoreDoc(snap.exists() ? snap.data() : null);
      setLoading(false);
    });

    return () => {
      unsubUser();
      unsubStore();
    };
  }, [user?.uid]);

  useEffect(() => {
    return onSnapshot(
      collection(db, "storefront_templates"),
      (snap) => {
        setTemplates(
          snap.docs
            .map((entry) => ({ id: entry.id, ...entry.data() }))
            .filter((row) => row.active !== false),
        );
      },
      (err) => {
        console.error("storefront template fetch failed", err);
        setTemplates([]);
      },
    );
  }, []);

  const packageState = useMemo(
    () => summarizeStorePackages(userDoc || {}),
    [userDoc],
  );
  const activePkg = packageState.hasActivePackage ? packageState.summary : null;
  const hasActiveStorePackage = packageState.hasActivePackage;
  const businessType =
    userDoc?.storeBusinessType ||
    storeDoc?.storeBusinessType ||
    storeDoc?.businessType ||
    "";

  useEffect(() => {
    const source = storeDoc || {};
    const userName =
      userDoc?.fullName ||
      userDoc?.displayName ||
      userDoc?.name ||
      user?.displayName ||
      (user?.email ? user.email.split("@")[0] : "") ||
      "";

    setDisplayName(source.displayName || userName || "");
    setSlug(source.slug || slugify(userName || user?.uid || ""));
    setBio(source.bio || userDoc?.bio || "");
    setStoreDescription(source.storeDescription || "");
    setCompanyProfile(normalizeCompanyProfile(source.companyProfile));
    setCategories(
      Array.isArray(source.categories) ? source.categories.join(", ") : "",
    );
    setProfileImageUrl(source.profileImageUrl || userDoc?.profilePicUrl || "");
    setVirtualStorefrontImageUrl(source.virtualStorefrontImageUrl || "");
    setCoverImageUrl(source.coverImageUrl || "");
    setPaymentQrCodeUrl(source.paymentDetails?.qrCodeUrl || "");
    setPaymentBankName(
      source.paymentDetails?.bankName || userDoc?.bankName || "",
    );
    setPaymentAccountHolder(
      source.paymentDetails?.accountHolder ||
        userDoc?.accountHolder ||
        userDoc?.fullName ||
        user?.displayName ||
        "",
    );
    setPaymentAccountNumber(
      source.paymentDetails?.accountNumber || userDoc?.bankAccount || "",
    );
    setPaymentBranch(
      source.paymentDetails?.branch || userDoc?.bankBranch || "",
    );
    setPaymentPhone(source.paymentDetails?.phone || userDoc?.phone || "");
    setPaymentWhatsapp(
      source.paymentDetails?.whatsapp || userDoc?.whatsapp || "",
    );
    setPaymentInstructions(source.paymentDetails?.instructions || "");
    setPaymentCashOnDelivery(!!source.paymentDetails?.cashOnDelivery);
    setSelectedTemplateId(source.storefrontTemplateId || "");
    setSelectedTemplateImageUrl(source.storefrontTemplateImageUrl || "");
    setSelectedTemplateType(source.storefrontTemplateType || "all");
    setSelectedTemplateTitleBackground(
      source.storefrontTemplateTitleBackground || "#2d1d16cc",
    );
    setSelectedTemplateTextColor(
      source.storefrontTemplateTextColor || "#ffffff",
    );
    setSelectedTemplateTitlePosition(
      source.storefrontTemplateTitlePosition || "top",
    );
    setSelectedTemplateDescription(source.storefrontTemplateDescription || "");
    setProfileImageFile(null);
    setCoverImageFile(null);
    setVirtualStorefrontImageFile(null);
    setPaymentQrCodeFile(null);
  }, [storeDoc, userDoc, user]);

  useEffect(() => {
    if (!profileImageFile) {
      setProfilePreviewUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(profileImageFile);
    setProfilePreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [profileImageFile]);

  useEffect(() => {
    if (!coverImageFile) {
      setCoverPreviewUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(coverImageFile);
    setCoverPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [coverImageFile]);

  useEffect(() => {
    if (!virtualStorefrontImageFile) {
      setVirtualStorefrontPreviewUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(virtualStorefrontImageFile);

    setVirtualStorefrontPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [virtualStorefrontImageFile]);

  useEffect(() => {
    if (!paymentQrCodeFile) {
      setPaymentQrPreviewUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(paymentQrCodeFile);
    setPaymentQrPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [paymentQrCodeFile]);

  const publication = storePublicationStatus(storeDoc);
  const previewUrl = slug ? `${publicBaseUrl}/store/${slugify(slug)}` : "";
  const previewCategories = normalizeCategories(categories);
  const previewName =
    displayName.trim() ||
    userDoc?.fullName ||
    user?.displayName ||
    (user?.email ? user.email.split("@")[0] : "Your Store");
  const previewProfile = profilePreviewUrl || profileImageUrl || "/avatar.png";
  const previewCover =
    coverPreviewUrl ||
    coverImageUrl ||
    "https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=1400&q=80";
  const previewVirtualStorefront =
    virtualStorefrontPreviewUrl || virtualStorefrontImageUrl || previewCover;
  const previewStorefrontImage =
    selectedTemplateImageUrl || previewCover || "/img/soft-luxury.png";
  const previewPaymentQr = paymentQrPreviewUrl || paymentQrCodeUrl || "";
  const previewStoreDescription = storeDescription?.trim();
  const previewModeTabs =
    businessType === "course"
      ? ["University", "Programs"]
      : businessType === "service"
        ? ["Services", "Bookings"]
        : ["Shopping", "Products"];
  const filteredTemplates = useMemo(() => {
    const normalizedType = String(businessType || "").toLowerCase();
    const sourceTemplates =
      templates.length > 0 ? templates : DEFAULT_STOREFRONT_TEMPLATES;
    if (!normalizedType) return sourceTemplates;
    return sourceTemplates.filter((template) => {
      const type = String(template.templateType || "all").toLowerCase();
      return type === "all" || type === normalizedType;
    });
  }, [businessType, templates]);

  const availableTemplates = filteredTemplates.length
    ? filteredTemplates
    : templates.length
      ? templates
      : DEFAULT_STOREFRONT_TEMPLATES;

  useEffect(() => {
    const fallback = availableTemplates[0] || DEFAULT_STOREFRONT_TEMPLATES[0];
    if (!selectedTemplateId && fallback) {
      setSelectedTemplateId(fallback.id);
      setSelectedTemplateImageUrl(fallback.imageUrl || "");
      setSelectedTemplateType(fallback.templateType || "all");
      setSelectedTemplateTitleBackground(
        fallback.titleBackground || "#2d1d16cc",
      );
      setSelectedTemplateTextColor(fallback.textColor || "#ffffff");
      setSelectedTemplateTitlePosition(fallback.titlePosition || "top");
      setSelectedTemplateDescription(fallback.description || "");
    }
  }, [availableTemplates, selectedTemplateId]);

  useEffect(() => {
    if (
      loading ||
      !user?.uid ||
      !userDoc ||
      !hasActiveStorePackage ||
      storeDoc ||
      initializingStore
    ) {
      return;
    }

    const defaultName =
      userDoc?.fullName ||
      userDoc?.displayName ||
      userDoc?.name ||
      user?.displayName ||
      (user?.email ? user.email.split("@")[0] : "") ||
      user.uid;
    const defaultSlug = slugify(defaultName || user.uid);

    if (!defaultSlug) return;

    setInitializingStore(true);

    runTransaction(db, async (transaction) => {
      const storeRef = doc(db, "stores", user.uid);
      const current = await transaction.get(storeRef);
      if (current.exists()) return;
      transaction.set(
        storeRef,
        {
          instructorId: user.uid,
          slug: defaultSlug,
          displayName: defaultName,
          published: true,
          bio: userDoc?.bio || "",
          storeDescription: "",
          categories: [],
          businessType,
          storeBusinessType: businessType,
          storefrontTemplateId: selectedTemplateId || "",
          storefrontTemplateImageUrl: selectedTemplateImageUrl || "",
          storefrontTemplateType: selectedTemplateType || "all",
          storefrontTemplateTitleBackground:
            selectedTemplateTitleBackground || "#2d1d16cc",
          storefrontTemplateTextColor: selectedTemplateTextColor || "#ffffff",
          storefrontTemplateTitlePosition:
            selectedTemplateTitlePosition || "top",
          storefrontTemplateDescription: selectedTemplateDescription || "",
          profileImageUrl: userDoc?.profilePicUrl || "",
          coverImageUrl: "",
          paymentDetails: {
            bankName: userDoc?.bankName || "",
            accountHolder:
              userDoc?.accountHolder ||
              userDoc?.fullName ||
              user?.displayName ||
              "",
            accountNumber: userDoc?.bankAccount || "",
            branch: userDoc?.bankBranch || "",
            phone: userDoc?.phone || "",
            whatsapp: userDoc?.whatsapp || "",
            instructions: "",
            qrCodeUrl: "",
            cashOnDelivery: false,
          },
          hasPaymentDetails: Boolean(
            userDoc?.bankName && userDoc?.bankAccount && (userDoc?.accountHolder || userDoc?.fullName),
          ),
          sponsorUid: userDoc?.referredByUid || userDoc?.sponsorUid || null,
          affiliateReferrerUid: userDoc?.referredByUid || userDoc?.affiliateReferrerUid || userDoc?.sponsorUid || null,
          referrerUserId: userDoc?.referredByUid || userDoc?.referrerUserId || userDoc?.sponsorUid || null,
          referralCode: userDoc?.referredByReferralCode || userDoc?.referralCode || null,
          referredByReferralCode: userDoc?.referredByReferralCode || null,
          storePackageStatus: "active",
          storePackageExpiresAt: activePkg?.expiresAt || null,
          hasStorePackage: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    })
      .catch((err) => {
        console.error(err);
        setError("Failed to initialize your store.");
      })
      .finally(() => setInitializingStore(false));
  }, [
    activePkg?.expiresAt,
    businessType,
    hasActiveStorePackage,
    initializingStore,
    loading,
    selectedTemplateDescription,
    selectedTemplateId,
    selectedTemplateImageUrl,
    selectedTemplateTextColor,
    selectedTemplateTitleBackground,
    selectedTemplateTitlePosition,
    selectedTemplateType,
    storeDoc,
    user,
    userDoc,
  ]);

  async function copyStoreLink() {
    if (!previewUrl) return;
    try {
      await navigator.clipboard.writeText(previewUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error(err);
      setError("Failed to copy store link.");
    }
  }

  async function saveStore() {
    if (companyMediaUploading) return;
    const profileError = validateCompanyProfile(companyProfile);
    if (profileError) {
      setError(profileError);
      setActiveStep(0);
      return;
    }
    if (!user?.uid) return;

    const finalSlug = slugify(slug || displayName || user.uid);
    if (!finalSlug) {
      setError("Please enter a store name or slug.");
      return;
    }

    setSaving(true);
    setMessage("");
    setError("");

    try {
      const storeRef = doc(db, "stores", user.uid);
      let nextProfileImageUrl = profileImageUrl.trim();
      let nextCoverImageUrl = coverImageUrl.trim();
      let nextVirtualStorefrontImageUrl = virtualStorefrontImageUrl.trim();
      let nextPaymentQrCodeUrl = paymentQrCodeUrl.trim();

      if (profileImageFile) {
        const profileRef = ref(storage, `stores/${user.uid}/profile.jpg`);
        await uploadBytes(profileRef, profileImageFile);
        nextProfileImageUrl = await getDownloadURL(profileRef);
      }

      if (coverImageFile) {
        const coverRef = ref(storage, `stores/${user.uid}/cover.jpg`);
        await uploadBytes(coverRef, coverImageFile);
        nextCoverImageUrl = await getDownloadURL(coverRef);
      }

      if (virtualStorefrontImageFile) {
        const storefrontRef = ref(
          storage,
          `stores/${user.uid}/virtual-storefront.jpg`,
        );

        await uploadBytes(storefrontRef, virtualStorefrontImageFile);

        nextVirtualStorefrontImageUrl = await getDownloadURL(storefrontRef);
      }

      if (paymentQrCodeFile) {
        const qrRef = ref(storage, `stores/${user.uid}/payment-qr.jpg`);
        await uploadBytes(qrRef, paymentQrCodeFile);
        nextPaymentQrCodeUrl = await getDownloadURL(qrRef);
      }

      const savedStore = await runTransaction(db, async (transaction) => {
        const existing = await transaction.get(storeRef);
        const payload = {
          instructorId: user.uid,
          slug: finalSlug,
          displayName: displayName.trim() || finalSlug,
          published: canPublishStore(existing.data(), hasActiveStorePackage),
          bio: bio.trim(),
          storeDescription: storeDescription || "",
          companyProfile: normalizeCompanyProfile(companyProfile),
          categories: normalizeCategories(categories),
          businessType,
          storeBusinessType: businessType,
          storefrontTemplateId: selectedTemplateId || "",
          storefrontTemplateImageUrl: selectedTemplateImageUrl || "",
          storefrontTemplateType: selectedTemplateType || businessType || "all",
          storefrontTemplateTitleBackground:
            selectedTemplateTitleBackground || "#2d1d16cc",
          storefrontTemplateTextColor: selectedTemplateTextColor || "#ffffff",
          storefrontTemplateTitlePosition:
            selectedTemplateTitlePosition || "top",
          storefrontTemplateDescription: selectedTemplateDescription || "",
          profileImageUrl: nextProfileImageUrl,
          coverImageUrl: nextCoverImageUrl,

          virtualStorefrontImageUrl: nextVirtualStorefrontImageUrl,
          paymentDetails: {
            bankName: paymentBankName.trim(),
            accountHolder: paymentAccountHolder.trim(),
            accountNumber: paymentAccountNumber.trim(),
            branch: paymentBranch.trim(),
            phone: paymentPhone.trim(),
            whatsapp: paymentWhatsapp.trim(),
            instructions: paymentInstructions.trim(),
            qrCodeUrl: nextPaymentQrCodeUrl,
            cashOnDelivery: paymentCashOnDelivery,
          },
          hasPaymentDetails: Boolean(
            paymentBankName.trim() &&
              paymentAccountHolder.trim() &&
              paymentAccountNumber.trim()
          ),
          sponsorUid:
            userDoc?.sponsorUid ||
            userDoc?.referredByUid ||
            existing.data()?.sponsorUid ||
            null,
          affiliateReferrerUid:
            userDoc?.affiliateReferrerUid ||
            userDoc?.referredByUid ||
            userDoc?.sponsorUid ||
            existing.data()?.affiliateReferrerUid ||
            null,
          referrerUserId:
            userDoc?.referrerUserId ||
            userDoc?.referredByUid ||
            userDoc?.sponsorUid ||
            existing.data()?.referrerUserId ||
            null,
          referralCode:
            userDoc?.referralCode ||
            userDoc?.referredByReferralCode ||
            existing.data()?.referralCode ||
            null,
          referredByReferralCode:
            userDoc?.referredByReferralCode ||
            existing.data()?.referredByReferralCode ||
            null,
          storePackageStatus: hasActiveStorePackage ? "active" : "inactive",
          storePackageExpiresAt: activePkg?.expiresAt || null,
          hasStorePackage: !!hasActiveStorePackage,
          updatedAt: serverTimestamp(),
          ...(existing.exists()
            ? {}
            : {
                createdAt: serverTimestamp(),
              }),
        };
        transaction.set(storeRef, payload, { merge: true });
        return { ...existing.data(), ...payload };
      });

      setProfileImageUrl(nextProfileImageUrl);
      setCoverImageUrl(nextCoverImageUrl);
      setPaymentQrCodeUrl(nextPaymentQrCodeUrl);
      setProfileImageFile(null);
      setCoverImageFile(null);
      setPaymentQrCodeFile(null);
      setSlug(finalSlug);
      const visibility = storePublicationStatus(savedStore);
      setMessage(`Store details saved. ${visibility.message}`);
    } catch (err) {
      console.error(err);
      setError(
        `Failed to save store settings. ${err.message || "Please try again."}`,
      );
    } finally {
      setSaving(false);
    }
  }

  function addCategory(value) {
    const next = value?.trim();
    if (!next) return;
    const merged = [...new Set([...previewCategories, next])];
    setCategories(merged.join(", "));
    setSelectedCategory("");
  }

  function removeCategory(value) {
    const next = previewCategories.filter((item) => item !== value);
    setCategories(next.join(", "));
  }

  if (loading) {
    return (
      <div className={`${embedded ? "" : "max-w-5xl mx-auto px-4 py-8"}`}>
        Loading...
      </div>
    );
  }

  if (!hasActiveStorePackage) {
    return (
      <div className={`${embedded ? "" : "max-w-5xl mx-auto px-4 py-8"}`}>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <div className="text-2xl font-bold">No active store package</div>
          <div className="mt-2 text-gray-700">
            Buy or renew a package before setting up your public store.
          </div>
          <Link
            to="/store-packages"
            className="inline-block mt-4 text-blue-600 hover:underline"
          >
            Go to Store Packages
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${embedded ? "space-y-6" : "max-w-5xl mx-auto px-4 py-8 space-y-6"}`}
    >
      <div>
        <h1 className="text-2xl font-bold">{title}</h1>
        <p className="mt-1 text-gray-600">{subtitle}</p>
      </div>

      <div
        role="status"
        className={`rounded-2xl border p-4 ${publication.live ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`}
      >
        <div className="font-semibold">
          Shop visibility: {publication.label}
        </div>
        <p className="mt-1 text-sm text-gray-700">{publication.message}</p>
      </div>

      <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
        <div className="font-semibold">
          Active package: {activePkg?.name || activePkg?.packageId}
        </div>
        <div className="mt-1 text-sm text-gray-700">
          Limits → Course: {Number(activePkg?.courseLimit || 0) || "∞"} •
          Product: {Number(activePkg?.productLimit || 0) || "∞"} • Service:{" "}
          {Number(activePkg?.serviceLimit || 0) || "∞"}
        </div>
        <div className="mt-1 text-xs font-semibold text-gray-700">
          Store mode: {getBusinessTypeLabel(businessType)}
        </div>
      </div>

      {(message || error) && (
        <div
          className={`rounded-xl border p-3 ${
            error
              ? "border-rose-200 bg-rose-50 text-rose-700"
              : "border-green-200 bg-green-50 text-green-700"
          }`}
        >
          {error || message}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[0.95fr,1.05fr]">
        <div className="space-y-6">
          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-700">
              Create your page
            </div>
            <div className="mt-2 text-2xl font-black text-slate-900">
              Build your store like a Facebook page
            </div>
            <div className="mt-2 text-sm text-slate-600">
              Fill a few simple steps, preview everything live, then save when
              it looks right.
            </div>

            <div className="mt-5 space-y-3">
              {STORE_SETUP_STEPS.map((step) => {
                const active = step.id === activeStep;
                const complete = step.id < activeStep;
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => setActiveStep(step.id)}
                    className={`flex w-full items-start gap-4 rounded-2xl border px-4 py-4 text-left transition ${
                      active
                        ? "border-blue-300 bg-blue-50 shadow-sm"
                        : "border-slate-200 bg-white hover:bg-slate-50"
                    }`}
                  >
                    <div
                      className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                        active
                          ? "bg-blue-600 text-white"
                          : complete
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {complete ? "✓" : step.id + 1}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">
                        {step.title}
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        {step.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-3xl border bg-white p-6 shadow-sm space-y-5">
            {activeStep === 0 && (
              <>
                <div>
                  <div className="text-lg font-bold text-slate-900">
                    Step 1: Store identity
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    This is the name, intro, and link people will see first.
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Store Name
                  </label>
                  <input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                    placeholder="Your public store name"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Store Slug
                  </label>
                  <input
                    value={slug}
                    onChange={(e) => setSlug(slugify(e.target.value))}
                    className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                    placeholder="your-store-name"
                  />
                  <div className="mt-2 text-xs text-gray-500">
                    Public URL:{" "}
                    {previewUrl || `${publicBaseUrl}/store/your-store-name`}
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Bio
                  </label>
                  <textarea
                    rows={5}
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                    placeholder="Tell customers about your store"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    About us — our story
                  </label>
                  <div className="mb-2 text-xs text-slate-500">
                    Add a richer about section with bold text, headings, bullet
                    lists, and links. This will appear in its own tab on the
                    public shop page.
                  </div>
                  <div
                    data-color-mode="light"
                    className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                  >
                    <MDEditor
                      value={storeDescription}
                      onChange={(value) => setStoreDescription(value || "")}
                      preview="edit"
                      height={280}
                      textareaProps={{
                        placeholder:
                          "Write a beautiful description about your shop, brand story, what you sell, delivery notes, trust details, and anything buyers should know.",
                      }}
                    />
                  </div>
                </div>
                <CompanyProfileEditor
                  onBusyChange={setCompanyMediaUploading}
                  value={companyProfile}
                  onChange={setCompanyProfile}
                  profileUrl={previewUrl ? `${previewUrl}/about` : ""}
                />

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Categories
                  </label>
                  <div className="flex flex-wrap gap-2 md:flex-nowrap">
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                    >
                      <option value="">Select a category</option>
                      {STORE_CATEGORY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => addCategory(selectedCategory)}
                      disabled={!selectedCategory}
                      className="rounded-xl border px-4 py-3 font-semibold disabled:opacity-40"
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {previewCategories.length > 0 ? (
                      previewCategories.map((category) => (
                        <button
                          key={category}
                          type="button"
                          onClick={() => removeCategory(category)}
                          className="rounded-full border bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                          title="Remove category"
                        >
                          {category} ×
                        </button>
                      ))
                    ) : (
                      <div className="text-xs text-slate-500">
                        Add categories from the dropdown to keep store labels
                        consistent.
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4">
                  <div className="text-sm font-semibold text-slate-900">
                    Next: choose your storefront style
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    After you finish the basic page details, move to Step 2 to
                    select the shop template buyers will see.
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveStep(1)}
                    className="mt-3 inline-flex items-center rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    Go to Storefront Styles
                  </button>
                </div>
              </>
            )}

            {activeStep === 1 && (
              <>
                <div>
                  <div className="text-lg font-bold text-slate-900">
                    Step 2: Storefront style and branding
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Shop Preview Image
                    </label>

                    <div className="mb-2 text-xs text-slate-500">
                      This image appears in View Shop on the home page and in the 3D mall. Without an image, the 3D storefront is used. Recommended size: 1600 × 1000 px.
                    </div>

                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) =>
                        setVirtualStorefrontImageFile(
                          e.target.files?.[0] || null,
                        )
                      }
                      className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                    />

                    <div className="mt-3 overflow-hidden rounded-2xl border bg-slate-950">
                      <img
                        src={previewVirtualStorefront}
                        alt="Virtual storefront preview"
                        className="h-64 w-full object-cover"
                      />
                    </div>

                    <p className="mt-2 text-xs text-slate-500">
                      Use a wide image showing the storefront, showroom,
                      products or brand experience. JPG, PNG or WebP, under 5
                      MB.
                    </p>
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    Pick a storefront template, then upload images that make
                    your page feel alive and trusted.
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        Choose storefront style
                      </div>
                      <div className="text-xs text-slate-500">
                        Your store name is previewed live on these admin-managed
                        templates.
                      </div>
                    </div>
                    <div className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                      {getBusinessTypeLabel(businessType)}
                    </div>
                  </div>

                  {templates.length === 0 ? (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      Using the built-in storefront styles right now. Admin
                      templates will appear here automatically once available.
                    </div>
                  ) : null}

                  <div className="grid gap-4 md:grid-cols-2">
                    {availableTemplates.map((template) => {
                      const active = selectedTemplateId === template.id;
                      return (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => {
                            setSelectedTemplateId(template.id);
                            setSelectedTemplateImageUrl(
                              template.imageUrl || "",
                            );
                            setSelectedTemplateType(
                              template.templateType || "all",
                            );
                            setSelectedTemplateTitleBackground(
                              template.titleBackground || "#2d1d16cc",
                            );
                            setSelectedTemplateTextColor(
                              template.textColor || "#ffffff",
                            );
                            setSelectedTemplateTitlePosition(
                              template.titlePosition || "top",
                            );
                            setSelectedTemplateDescription(
                              template.description || "",
                            );
                          }}
                          className={`overflow-hidden rounded-3xl border text-left shadow-sm transition ${
                            active
                              ? "border-blue-400 ring-2 ring-blue-200"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <div className="relative h-40 bg-slate-900">
                            {template.imageUrl ? (
                              <img
                                src={template.imageUrl}
                                alt={template.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full bg-gradient-to-br from-slate-800 via-blue-700 to-fuchsia-700" />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/10" />
                            <div
                              className={`absolute ${storefrontOverlayClass(
                                template.titlePosition || "top",
                              )}`}
                            >
                              <div
                                className="rounded-[18px] px-4 py-2 text-lg font-black shadow-lg backdrop-blur"
                                style={{
                                  color: template.textColor || "#ffffff",
                                  background:
                                    template.titleBackground || "#2d1d16cc",
                                }}
                              >
                                {previewName}
                              </div>
                            </div>
                          </div>
                          <div className="px-4 py-4">
                            <div className="font-semibold text-slate-900">
                              {template.name}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              {template.description ||
                                "Template used for public shopping-complex cards."}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-[0.9fr,1.1fr]">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Profile Image
                    </label>
                    <div className="mb-2 text-xs text-slate-500">
                      Recommended size: 800 x 800 px (square image)
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setProfileImageFile(e.target.files?.[0] || null)
                      }
                      className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                    />
                    <div className="mt-3 flex items-center gap-3">
                      <img
                        src={previewProfile}
                        alt="Profile preview"
                        className="h-20 w-20 rounded-full border object-cover"
                      />
                      <div className="text-sm text-slate-600">
                        This appears as your page avatar.
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Cover Image
                    </label>
                    <div className="mb-2 text-xs text-slate-500">
                      Recommended size: 1500 x 500 px (wide banner)
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setCoverImageFile(e.target.files?.[0] || null)
                      }
                      className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                    />
                    <img
                      src={previewCover}
                      alt="Cover preview"
                      className="mt-3 h-36 w-full rounded-2xl border object-cover"
                    />
                  </div>
                </div>
              </>
            )}

            {activeStep === 2 && (
              <>
                <div>
                  <div className="text-lg font-bold text-slate-900">
                    Step 3: Payment details
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    Buyers will see these details at checkout, so keep them
                    clear and complete.
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Bank Name
                    </label>
                    <input
                      value={paymentBankName}
                      onChange={(e) => setPaymentBankName(e.target.value)}
                      className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                      placeholder="Commercial Bank"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Account Holder
                    </label>
                    <input
                      value={paymentAccountHolder}
                      onChange={(e) => setPaymentAccountHolder(e.target.value)}
                      className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                      placeholder="Account holder name"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Account Number
                    </label>
                    <input
                      value={paymentAccountNumber}
                      onChange={(e) => setPaymentAccountNumber(e.target.value)}
                      className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                      placeholder="1234567890"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Branch
                    </label>
                    <input
                      value={paymentBranch}
                      onChange={(e) => setPaymentBranch(e.target.value)}
                      className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                      placeholder="Main Branch"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Contact Phone
                    </label>
                    <input
                      value={paymentPhone}
                      onChange={(e) => setPaymentPhone(e.target.value)}
                      className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                      placeholder="+94 7XXXXXXXX"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      WhatsApp
                    </label>
                    <input
                      value={paymentWhatsapp}
                      onChange={(e) => setPaymentWhatsapp(e.target.value)}
                      className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                      placeholder="+94 7XXXXXXXX"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Buyer Instructions
                  </label>
                  <textarea
                    rows={3}
                    value={paymentInstructions}
                    onChange={(e) => setPaymentInstructions(e.target.value)}
                    className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                    placeholder="Ask buyers to include the order id in their transfer reference."
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Payment QR Code
                  </label>
                  <div className="mb-2 text-xs text-slate-500">
                    Upload the QR image buyers can scan to pay you faster.
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setPaymentQrCodeFile(e.target.files?.[0] || null)
                    }
                    className="w-full rounded-xl border px-4 py-3 outline-none focus:border-blue-500"
                  />
                  {(previewPaymentQr || paymentQrCodeUrl) && (
                    <div className="mt-3 rounded-2xl border bg-slate-50 p-3">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                        QR Preview
                      </div>
                      <img
                        src={previewPaymentQr || paymentQrCodeUrl}
                        alt="Payment QR preview"
                        className="h-40 w-40 rounded-2xl border bg-white object-contain"
                      />
                    </div>
                  )}
                </div>

                <label className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={paymentCashOnDelivery}
                    onChange={(e) => setPaymentCashOnDelivery(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>
                    <span className="block font-semibold text-slate-900">
                      Cash on delivery available
                    </span>
                    <span className="mt-1 block text-slate-600">
                      Turn this on if buyers can pay you when the product is
                      delivered. This will be shown clearly in the user portal.
                    </span>
                  </span>
                </label>
              </>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setActiveStep((value) => Math.max(0, value - 1))
                  }
                  disabled={activeStep === 0}
                  className="rounded-xl border px-4 py-2 font-semibold disabled:opacity-40"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setActiveStep((value) =>
                      Math.min(STORE_SETUP_STEPS.length - 1, value + 1),
                    )
                  }
                  disabled={activeStep === STORE_SETUP_STEPS.length - 1}
                  className="rounded-xl border px-4 py-2 font-semibold disabled:opacity-40"
                >
                  Next
                </button>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={saveStore}
                  disabled={saving || companyMediaUploading}
                  className={`rounded-xl px-5 py-2.5 font-semibold text-white ${
                    saving ? "bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {saving ? "Saving..." : "Save Store"}
                </button>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={`rounded-xl border px-5 py-2.5 font-semibold ${
                    previewUrl
                      ? "hover:bg-gray-50"
                      : "pointer-events-none opacity-50"
                  }`}
                >
                  Open Live Store
                </a>
                <button
                  type="button"
                  onClick={copyStoreLink}
                  disabled={!previewUrl}
                  className={`rounded-xl border px-5 py-2.5 font-semibold ${
                    previewUrl
                      ? "hover:bg-gray-50"
                      : "pointer-events-none opacity-50"
                  }`}
                >
                  {copied ? "Copied" : "Copy Link"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-3xl border bg-white p-5 shadow-sm xl:sticky xl:top-6">
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">
              Live Preview
            </div>
            <div className="mt-2 text-2xl font-black text-slate-900">
              This is how buyers will see your store
            </div>
            <div className="mt-2 text-sm text-slate-600">
              The preview updates instantly while you edit your store.
            </div>

            <div className="mt-5 overflow-hidden rounded-[28px] border bg-slate-50 shadow-sm">
              <img
                src={previewCover}
                alt="Store cover preview"
                className="h-44 w-full object-cover"
              />

              <div className="px-4 pb-5">
                <img
                  src={previewProfile}
                  alt="Store profile preview"
                  className="-mt-12 h-24 w-24 rounded-full border-4 border-white bg-white object-cover shadow"
                />

                <div className="mt-3 text-2xl font-bold text-slate-900">
                  {previewName}
                </div>
                <div className="mt-2 whitespace-pre-line text-sm text-slate-600">
                  {bio.trim() ||
                    "Add a short store bio so buyers understand what you offer."}
                </div>

                {previewStoreDescription ? (
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      About tab preview
                    </div>
                    <div
                      data-color-mode="light"
                      className="prose prose-sm mt-3 max-w-none text-slate-700"
                    >
                      <MDEditor.Markdown source={previewStoreDescription} />
                    </div>
                  </div>
                ) : null}

                {previewCategories.length > 0 ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {previewCategories.map((category) => (
                      <span
                        key={category}
                        className="rounded-full border bg-white px-3 py-1 text-xs font-medium text-slate-700"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                ) : null}

                {paymentCashOnDelivery ? (
                  <div className="mt-4 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                    Cash on delivery available
                  </div>
                ) : null}

                {previewPaymentQr || paymentQrCodeUrl ? (
                  <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">
                      Buyer payment QR
                    </div>
                    <div className="mt-2 text-sm text-slate-600">
                      Buyers will see this QR code during checkout.
                    </div>
                    <img
                      src={previewPaymentQr || paymentQrCodeUrl}
                      alt="Buyer payment QR preview"
                      className="mt-3 h-32 w-32 rounded-2xl border bg-white object-contain"
                    />
                  </div>
                ) : null}

                <div className="mt-5 rounded-2xl border bg-white p-4">
                  <div className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Public storefront card preview
                  </div>
                  <div className="overflow-hidden rounded-[28px] border border-white/70 bg-[#1b1422]/90 text-white shadow-[0_18px_45px_-18px_rgba(0,0,0,0.45)]">
                    <div className="relative h-48">
                      <img
                        src={previewStorefrontImage}
                        alt="Storefront template preview"
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#140f1f] via-[#140f1f]/30 to-transparent" />
                      <div
                        className={`absolute ${storefrontOverlayClass(
                          selectedTemplateTitlePosition,
                        )}`}
                      >
                        <div
                          className="rounded-[18px] px-4 py-2 text-center text-lg font-black shadow-lg backdrop-blur"
                          style={{
                            color: selectedTemplateTextColor,
                            background: selectedTemplateTitleBackground,
                          }}
                        >
                          {previewName}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 px-4 py-4">
                      <div className="h-14 w-14 overflow-hidden rounded-full border-4 border-white/20 bg-white/10">
                        <img
                          src={previewProfile}
                          alt="Store avatar preview"
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="line-clamp-1 text-sm font-semibold text-white/95">
                          {selectedTemplateDescription ||
                            bio.trim() ||
                            "Discover premium products, services, and store experiences."}
                        </div>
                        <div className="mt-2 inline-flex items-center gap-2 text-xs text-amber-300">
                          <span>★</span> 4.8 Featured
                        </div>
                      </div>
                    </div>

                    <div className="px-4 pb-4">
                      <div className="inline-flex items-center rounded-2xl bg-[#f2cb74] px-4 py-2 text-sm font-bold text-[#342410] shadow">
                        Visit Shop
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {previewModeTabs.map((tab, index) => (
                      <span
                        key={tab}
                        className={`rounded-full px-3 py-2 text-xs font-semibold ${
                          index === 0
                            ? "bg-slate-900 text-white"
                            : "border border-slate-200 bg-white text-slate-600"
                        }`}
                      >
                        {tab}
                      </span>
                    ))}
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {PREVIEW_ITEMS.filter((item) => {
                      if (businessType === "course")
                        return item.subtitle === "Course";
                      if (businessType === "service")
                        return item.subtitle === "Service";
                      if (businessType === "product")
                        return item.subtitle === "Product";
                      return true;
                    }).map((item) => (
                      <div
                        key={item.id}
                        className="overflow-hidden rounded-2xl border bg-white"
                      >
                        <div className="h-28 bg-gradient-to-br from-slate-200 via-slate-100 to-slate-300" />
                        <div className="space-y-1 p-3">
                          <div className="line-clamp-2 text-sm font-semibold text-slate-900">
                            {item.title}
                          </div>
                          <div className="text-xs text-slate-500">
                            {item.subtitle}
                          </div>
                          <div className="text-sm font-semibold text-slate-900">
                            {item.price}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-900">
                        Public URL preview
                      </div>
                      <div className="mt-1 break-all text-sm text-blue-700">
                        {previewUrl || `${publicBaseUrl}/store/your-store-name`}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={copyStoreLink}
                      disabled={!previewUrl}
                      className={`shrink-0 rounded-xl border px-3 py-2 text-sm font-semibold ${
                        previewUrl
                          ? "bg-white hover:bg-slate-50"
                          : "pointer-events-none opacity-50"
                      }`}
                    >
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>

                  <div className="mt-4 flex flex-col gap-4 rounded-2xl border border-blue-200 bg-white/80 p-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">
                        Shop QR code
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        People can scan this QR code and go straight to your
                        public shop page.
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="rounded-2xl border border-blue-100 bg-white p-3 shadow-sm">
                        <QRCodeSVG
                          value={
                            previewUrl ||
                            `${publicBaseUrl}/store/${slugify(slug || previewName)}`
                          }
                          size={124}
                          bgColor="#ffffff"
                          fgColor="#243043"
                          includeMargin
                        />
                      </div>
                      <div className="max-w-[220px] text-xs leading-5 text-slate-500">
                        Print it, share it, or keep it on your counter so
                        customers can open your shop quickly.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StoreSettings() {
  return <StoreSettingsPanel />;
}
